import type { StateCreator } from 'zustand';
import type { Account, LogoOption } from '../types';
import { accountsApi } from '../services/accountsApi';

export interface AccountsSlice {
  accounts: Account[];
  accountsTotal: number;
  accountsPage: number;
  accountsLoading: boolean;
  accountsFilter: { niche?: string; search?: string; platform?: string };
  showAllLangs: boolean;
  logoGenerating: boolean;
  logoOptions: LogoOption[];
  toggleShowAllLangs: () => void;
  fetchAccounts: (params?: { niche?: string; search?: string; page?: number; lang?: string; platform?: string }) => Promise<void>;
  addAccount: (data: { username: string; niche?: string; account_type?: string; platform?: string }) => Promise<void>;
  updateAccount: (username: string, data: Partial<Account>) => Promise<void>;
  hideAccount: (username: string, reason?: string) => Promise<void>;
  bulkUpdateAccounts: (accountIds: number[], updates: Record<string, unknown>) => Promise<void>;
  setAccountsFilter: (filter: { niche?: string; search?: string; platform?: string }) => void;
  uploadAccountLogo: (username: string, file: File) => Promise<string>;
  removeLogoBackground: (username: string, imageData: string) => Promise<string>;
  enrichLogoPrompt: (username: string) => Promise<string>;
  generateLogos: (username: string, prompt: string) => Promise<void>;
  selectLogo: (username: string, svg: string) => Promise<string>;
  deleteAccountLogo: (username: string) => Promise<void>;
}

export const createAccountsSlice: StateCreator<AccountsSlice> = (set, get) => ({
  accounts: [],
  accountsTotal: 0,
  accountsPage: 1,
  accountsLoading: false,
  accountsFilter: {},
  showAllLangs: false,
  logoGenerating: false,
  logoOptions: [],

  toggleShowAllLangs: () => {
    set((s) => ({ showAllLangs: !s.showAllLangs }));
  },

  fetchAccounts: async (params) => {
    set({ accountsLoading: true });
    try {
      const filter = get().accountsFilter;
      const res = await accountsApi.list({
        niche: params?.niche ?? filter.niche,
        search: params?.search ?? filter.search,
        page: params?.page ?? 1,
        lang: params?.lang,
        platform: params?.platform ?? filter.platform,
      });
      set({
        accounts: res.accounts,
        accountsTotal: res.total,
        accountsPage: res.page,
        accountsLoading: false,
      });
    } catch {
      set({ accountsLoading: false });
    }
  },

  addAccount: async (data) => {
    await accountsApi.add(data);
    get().fetchAccounts();
  },

  updateAccount: async (username, data) => {
    await accountsApi.update(username, data);
    get().fetchAccounts();
  },

  hideAccount: async (username, reason) => {
    await accountsApi.hide(username, reason);
    get().fetchAccounts();
  },

  bulkUpdateAccounts: async (accountIds, updates) => {
    await accountsApi.bulkUpdate(accountIds, updates);
    get().fetchAccounts();
  },

  setAccountsFilter: (filter) => {
    set({ accountsFilter: filter });
  },

  uploadAccountLogo: async (username, file) => {
    const res = await accountsApi.uploadLogo(username, file);
    get().fetchAccounts();
    return res.logo_url;
  },

  removeLogoBackground: async (username, imageData) => {
    const res = await accountsApi.removeBackground(username, imageData);
    return res.image_data;
  },

  enrichLogoPrompt: async (username) => {
    const res = await accountsApi.enrichLogoPrompt(username);
    return res.prompt;
  },

  generateLogos: async (username, prompt) => {
    set({ logoGenerating: true, logoOptions: [] });
    try {
      const res = await accountsApi.generateLogos(username, prompt);
      set({ logoOptions: res.options });
    } finally {
      set({ logoGenerating: false });
    }
  },

  selectLogo: async (username, svg) => {
    const res = await accountsApi.selectLogo(username, svg);
    get().fetchAccounts();
    return res.logo_url;
  },

  deleteAccountLogo: async (username) => {
    await accountsApi.deleteLogo(username);
    get().fetchAccounts();
  },
});
